import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import {
  type NotificationPolicy,
  type NotificationPolicyCreatedValues,
  NotificationPolicyObject,
} from '~/models/dataSubscriptions';

import { queryKeysDataSubscription } from './queryKeys';

type NotificationPolicyResponse = {
  items: NotificationPolicy[];
};

/**
 * Fetches a list of notification policy from the API.
 * This function retrieves notification policy and optionally maps them into instances of 'NotificationPolicy'
 * if 'initNotificationPolicies' is true.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/get_all_notification_policy_data_subscription_notification_policy_get
 * @returns {Promise<NotificationPolicyResponse>} Response from the server.
 * @throws {Error} If the API request fails.
 */
export const fetchNotificationPolicies = async (
  initNotificationPolicies = true,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_SUBSCRIPTION.GET_NOTIFICATION_POLICY())
    .json<NotificationPolicyResponse>();

  if (initNotificationPolicies) {
    return (
      response.items?.map((item) => NotificationPolicyObject.create(item)) ?? []
    );
  }

  return response.items ?? [];
};

/**
 * Custom React Query hook to fetch notification policy.
 */
export const useQueryNotificationPolicies = (
  initNotificationPolicies = true,
  options?: Omit<
    UseQueryOptions<NotificationPolicy[] | NotificationPolicyCreatedValues[]>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryFn: async () => fetchNotificationPolicies(initNotificationPolicies),
    queryKey: queryKeysDataSubscription.getAllNotificationPolicies(
      initNotificationPolicies,
    ),
    ...options,
  });
